/**
 * Firebase error codes into sentences a person can act on.
 *
 * Kept free of any Firebase import so the sign-in page can use it without
 * loading Firestore. lib/hooks.ts re-exports both functions, so callers that
 * import them from there are unchanged.
 */

import { describeCallableError } from './callableError';

/** Firebase Auth codes into sentences a first-time user can act on. */
export function describeAuthError(code: string): string {
  switch (code) {
    case 'auth/email-already-in-use':
      return 'That email already has an account. Sign in instead.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email and password do not match.';
    case 'auth/weak-password':
      return 'Choose a longer password, at least 8 characters.';
    case 'auth/invalid-email':
      return 'That does not look like an email address.';
    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes and try again.';
    case 'auth/network-request-failed':
      return 'No connection. Check your network and try again.';
    default:
      return 'Sign-in did not work. Try again.';
  }
}

/** Human wording for the Firestore error codes users actually hit. */
export function describeFirestoreError(err: unknown): string {
  const code = (err as { code?: string })?.code ?? '';
  // Callable errors (functions/*): the server's own sentence where it wrote
  // one, never the SDK's code or its "[500]" suffix (callableError.ts).
  if (code.startsWith('functions/')) {
    return describeCallableError(code.slice('functions/'.length), (err as { message?: string })?.message);
  }
  if (code.startsWith('auth/')) return describeAuthError(code);
  if (code.includes('permission-denied')) {
    // Firestore returns this code for two different situations and the
    // client cannot tell them apart: the caller's role is not allowed, or
    // the workspace is at its plan ceiling. The old sentence named only
    // the first and told the reader to ask an administrator, which is
    // absurd when the reader IS the administrator and the real cause is a
    // full plan. Name both and point at the one screen that settles it.
    //
    // The metered forms check the ceiling before they write (see
    // planCeiling in @claripick/shared), so this is the fallback for a
    // path that did not, not the primary way anyone learns about a limit.
    return 'That did not save. It may be your role, or your plan may be full. Workspace settings shows both.';
  }
  if (code.includes('failed-precondition')) {
    // On a deployed Firestore this means a composite index is missing from
    // firestore.indexes.json. Nothing about that resolves by waiting, so
    // "try again shortly" sent the user to sit and watch a page that was
    // never going to load. It is ours to fix, and saying so is the only
    // useful thing this sentence can do.
    return 'This list cannot load right now. It is a fault on our side, not something you can fix. Tell support which page you were on.';
  }
  if (code.includes('unavailable')) {
    return 'No connection right now. This will load when you are back online.';
  }
  // Deliberately NOT err.message. Firebase's own English ("Missing or
  // insufficient permissions.", "The query requires an index.") was
  // preferred over the sentence written for the reader, so the fallback
  // that exists for exactly this case never ran. The code stays
  // recoverable: console.error already fires on every read and write path.
  return 'This could not be loaded. Refresh the page; if it happens again, ask a workspace administrator.';
}
